// 5 KPI-fliser (mockup): Utestående lån, Klientkonto, Sum gjeld, Egenkapital, Årsresultat.
// Oblinor-tall fra eksponering, regnskaps-tall fra nyeste Brreg-år. null → "—".
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { formatKpi } from "../api/format";
import type { ExposureResponse, RegnskapAar } from "../api/types";

interface KpiTilesProps {
  eksponering: ExposureResponse | undefined;
  regnskap: RegnskapAar[] | undefined;
  isLoading: boolean;
}

function Tile({
  value,
  label,
  source,
  neg,
}: {
  value: number | null | undefined;
  label: string;
  source: string;
  neg?: boolean;
}) {
  const { value: v, unit } = formatKpi(value);
  const isNeg = neg ?? (value != null && value < 0);
  return (
    <div className="glass px-4 pb-3.5 pt-4">
      <div className={cn("kpi-v tab-num text-[22px]", isNeg && "neg")}>
        {v}
        {unit && <small className="ml-1 text-[12px] font-medium text-[var(--sub)]">{unit}</small>}
      </div>
      <div className="mt-1.5 text-[12px] text-[var(--sub)]">{label}</div>
      <div className="mt-[3px] text-[10px] tracking-[0.02em] text-[var(--faint)]">{source}</div>
    </div>
  );
}

export function KpiTiles({ eksponering, regnskap, isLoading }: KpiTilesProps) {
  if (isLoading) {
    return (
      <div className="mb-[18px] grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-[86px] rounded-[20px] bg-[rgba(120,60,10,0.1)]" />
        ))}
      </div>
    );
  }

  const latest = regnskap && regnskap.length ? regnskap[regnskap.length - 1] : null;
  const brregSrc = latest ? `Brreg ${latest.aar}` : "Brreg";

  return (
    <div className="mb-[18px] grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
      <Tile value={eksponering?.sumUtestaaende ?? null} label="Utestående lån" source="Oblinor" />
      <Tile value={eksponering?.klientkonto ?? null} label="Klientkonto" source="Oblinor" />
      <Tile value={latest?.sumGjeld ?? null} label="Sum gjeld" source={brregSrc} />
      <Tile value={latest?.sumEgenkapital ?? null} label="Egenkapital" source={brregSrc} />
      <Tile value={latest?.aarsresultat ?? null} label="Årsresultat" source={brregSrc} />
    </div>
  );
}
